import { get, post } from 'api/client';
import config from 'config';
import { getExtendedTimeout } from 'utils/request';
import type {
  TriggerSyncParams,
  SyncBatch,
  OverviewParams,
  OverviewResponse,
  PerformanceParams,
  PerformanceResponse,
  AudienceParams,
  AudienceResponse,
  BestTimeToPostParams,
  BestTimeToPostResponse,
  ContentListParams,
  ContentListApiResponse,
  PostDetailParams,
  PostDetailResponse,
  PageMetricsResponse,
  EnablePageResponse,
  PageHealth,
  ExportParams,
  PerformanceV2Params,
  PerformanceV2Response,
} from './types';

const BASE_PATH = '/analytics';

const NEW_ANALYTICS_AXIOS_CONFIG = {
  baseURL: config.API_SERVICE_ENDPOINT,
  timeout: getExtendedTimeout(5),
} as const;

const NEW_ANALYTICS_EXPORT_AXIOS_CONFIG = {
  baseURL: config.API_SERVICE_ENDPOINT,
  timeout: getExtendedTimeout(10),
} as const;

// Query arrays like docs §5 Performance example: page_ids[]=123&page_ids[]=456&since=…
const NEW_ANALYTICS_ARRAY_QUERY_KEYS = new Set([
  'page_ids',
  'metrics',
  'entity_types',
]);

function serializeNewAnalyticsParams(params: Record<string, unknown>): string {
  const segments: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue;
      }
      const queryName = NEW_ANALYTICS_ARRAY_QUERY_KEYS.has(key)
        ? `${key}[]`
        : encodeURIComponent(key);
      for (const item of value) {
        segments.push(`${queryName}=${encodeURIComponent(String(item))}`);
      }
      continue;
    }
    segments.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );
  }
  return segments.join('&');
}

function newAnalyticsGetParams(
  params: Record<string, string | number | string[] | number[] | undefined>,
) {
  return {
    params,
    paramsSerializer: serializeNewAnalyticsParams,
  };
}

function buildPageParams(params: {
  page_ids?: number[];
  preset_id?: number;
}): Record<string, number | number[] | undefined> {
  if (params.preset_id !== undefined) {
    return { preset_id: params.preset_id };
  }
  if (params.page_ids !== undefined && params.page_ids.length > 0) {
    return { page_ids: params.page_ids };
  }
  return {};
}

const NewAnalyticsApi = {
  triggerSync(params: TriggerSyncParams): Promise<SyncBatch[]> {
    return post<SyncBatch[]>(
      `${BASE_PATH}/sync`,
      {
        page_ids: params.page_ids,
        since: params.since,
        until: params.until,
        api_version: params.api_version,
      },
      NEW_ANALYTICS_AXIOS_CONFIG,
    );
  },

  getSyncBatchStatus(batchId: string): Promise<SyncBatch> {
    return get<SyncBatch>(
      `${BASE_PATH}/sync/batch/${batchId}`,
      NEW_ANALYTICS_AXIOS_CONFIG,
    );
  },

  listSyncJobs(pageId: number): Promise<SyncBatch[]> {
    return get<SyncBatch[]>(
      `${BASE_PATH}/sync/page/${pageId}`,
      NEW_ANALYTICS_AXIOS_CONFIG,
    );
  },

  getOverview(params: OverviewParams): Promise<OverviewResponse> {
    return get<OverviewResponse>(`${BASE_PATH}/overview`, {
      ...newAnalyticsGetParams({
        ...buildPageParams(params),
        since: params.since,
        until: params.until,
        timezone: params.timezone,
        compare_since: params.compare_since,
        compare_until: params.compare_until,
      }),
      ...NEW_ANALYTICS_AXIOS_CONFIG,
    });
  },

  /**
   * TEMPORARY — v2 Instagram performance, served live from the provider.
   * Removed when v2 is folded into `getPerformance`.
   */
  getPerformanceV2(
    params: PerformanceV2Params,
  ): Promise<PerformanceV2Response> {
    return get<PerformanceV2Response>(`${BASE_PATH}/v2/performance`, {
      ...newAnalyticsGetParams({
        page_ids: params.page_ids,
        since: params.since,
        until: params.until,
        metrics: params.metrics,
        compare_since: params.compare_since,
        compare_until: params.compare_until,
      }),
      ...NEW_ANALYTICS_AXIOS_CONFIG,
    });
  },

  getPerformance(params: PerformanceParams): Promise<PerformanceResponse> {
    return get<PerformanceResponse>(`${BASE_PATH}/performance`, {
      ...newAnalyticsGetParams({
        ...buildPageParams(params),
        since: params.since,
        until: params.until,
        timezone: params.timezone,
        compare_since: params.compare_since,
        compare_until: params.compare_until,
        metrics: params.metrics,
        include: params.include,
      }),
      ...NEW_ANALYTICS_AXIOS_CONFIG,
    });
  },

  getAudience(params: AudienceParams): Promise<AudienceResponse> {
    return get<AudienceResponse>(`${BASE_PATH}/performance/audience`, {
      ...newAnalyticsGetParams(buildPageParams(params)),
      ...NEW_ANALYTICS_AXIOS_CONFIG,
    });
  },

  getBestTimeToPost(
    params: BestTimeToPostParams,
  ): Promise<BestTimeToPostResponse> {
    return get<BestTimeToPostResponse>(
      `${BASE_PATH}/performance/best-time-to-post`,
      {
        ...newAnalyticsGetParams({
          ...buildPageParams(params),
          since: params.since,
          until: params.until,
          timezone: params.timezone,
        }),
        ...NEW_ANALYTICS_AXIOS_CONFIG,
      },
    );
  },

  getContent(params: ContentListParams): Promise<ContentListApiResponse> {
    return get<ContentListApiResponse>(`${BASE_PATH}/content`, {
      ...newAnalyticsGetParams({
        ...buildPageParams(params),
        since: params.since,
        until: params.until,
        timezone: params.timezone,
        sort_by: params.sort_by,
        sort_dir: params.sort_dir,
        entity_types: params.entity_types,
        promotion_type: params.promotion_type,
        granularity: params.granularity,
        per_page: params.per_page,
        page: params.page,
      }),
      ...NEW_ANALYTICS_AXIOS_CONFIG,
    });
  },

  getPostDetail(
    postId: number,
    params?: PostDetailParams,
  ): Promise<PostDetailResponse> {
    return get<PostDetailResponse>(`${BASE_PATH}/content/posts/${postId}`, {
      params: params?.include ? { include: params.include } : undefined,
      ...NEW_ANALYTICS_AXIOS_CONFIG,
    });
  },

  exportPostComments(entityId: number): Promise<Blob> {
    return get<Blob>(`${BASE_PATH}/content/posts/${entityId}/comments/export`, {
      responseType: 'blob',
      ...NEW_ANALYTICS_EXPORT_AXIOS_CONFIG,
    });
  },

  getPageMetrics(pageId: number): Promise<PageMetricsResponse> {
    return get<PageMetricsResponse>(
      `${BASE_PATH}/pages/${pageId}/metrics`,
      NEW_ANALYTICS_AXIOS_CONFIG,
    );
  },

  enablePage(pageId: number): Promise<EnablePageResponse> {
    return post<EnablePageResponse>(
      `${BASE_PATH}/pages/${pageId}/enable`,
      {},
      NEW_ANALYTICS_AXIOS_CONFIG,
    );
  },

  getHealth(): Promise<PageHealth[]> {
    return get<PageHealth[]>(`${BASE_PATH}/health`, NEW_ANALYTICS_AXIOS_CONFIG);
  },

  exportData(params: ExportParams): Promise<Blob> {
    return get<Blob>(`${BASE_PATH}/export`, {
      ...newAnalyticsGetParams({
        ...buildPageParams(params),
        since: params.since,
        until: params.until,
        timezone: params.timezone,
        section: params.section,
        platform: params.platform,
        compare_since: params.compare_since,
        compare_until: params.compare_until,
      }),
      responseType: 'blob',
      ...NEW_ANALYTICS_EXPORT_AXIOS_CONFIG,
    });
  },
};

export const newAnalyticsHttpApi = NewAnalyticsApi;

export default NewAnalyticsApi;
